import React, { useState } from 'react';
import './IntroModal.css';

/**
 * IntroModal
 * Shown once per browser session (or once ever, if you switch to localStorage)
 * when the app first loads. Presents the FutureSoulmates intro / how-it-works
 * copy in a T&C-style popup with an "I Agree" / "Get Started" action.
 *
 * Usage: render <IntroModal /> near the top of App.js, above <Routes>.
 */

const STORAGE_KEY = 'fs_intro_seen';

const IntroModal = () => {
  const [visible, setVisible] = useState(() => {
    try {
      return !sessionStorage.getItem(STORAGE_KEY);
    } catch {
      return true;
    }
  });
  const [agreed, setAgreed] = useState(false);

  if (!visible) return null;

  const handleClose = () => {
    try {
      sessionStorage.setItem(STORAGE_KEY, 'true');
    } catch {
      /* ignore storage errors (e.g. private browsing) */
    }
    setVisible(false);
  };

  return (
    <div className="intro-modal-overlay" role="dialog" aria-modal="true" aria-labelledby="intro-modal-title">
      <div className="intro-modal">
        <div className="intro-modal-scroll">
          <h1 id="intro-modal-title" className="intro-modal-brand">FutureSoulmates™</h1>
          <p className="intro-modal-tagline">Discover Compatibility Beyond the Profile</p>

          <p>
            FutureSoulmates uses intelligent compatibility matching to help you find
            people who may truly fit your life — not just your preferences.
          </p>

          <h2>How It Works</h2>
          <p>
            Tell us about your personality, values, lifestyle, interests, relationship
            goals, and what matters most to you. Our matching system evaluates
            compatibility across multiple dimensions and identifies people with the
            strongest potential.
          </p>

          <h3>Stage 1 — Get Started</h3>
          <ol className="intro-modal-steps">
            <li>Meet the person online or in person, then schedule a video meeting.</li>
            <li>Download the app and complete your profile. Have your date do the same.</li>
            <li>Schedule a mutually convenient time.</li>
            <li>Sit down together and go through the guided question sessions.</li>
            <li>
              Your smartwatch, voice, and video readings will be recorded by our
              software. At the end of each session you'll receive downloadable
              insights on your potential compatibility.
            </li>
            <li>
              Start with a 60-minute first session to see if you're a match. If yes,
              move to a second 60-minute session to verify compatibility. If that
              goes well — time to meet in person!
            </li>
          </ol>

          <h3>Stage 2 — Deeper Insight (Optional)</h3>
          <p>
            If things are going well, you can upload compatibility DNA results to
            help fine-tune your match. <strong>Compatibility DNA readings are still
            in a research phase and may not be accurate</strong>, but can offer
            general direction.
          </p>

          <h3>Stage 3 — Family Planning (Optional)</h3>
          <p>
            If you're both looking to take the next step, you can add an extended
            DNA panel intended to help identify potential inherited health risks for
            future children.
          </p>

          <h2>Why FutureSoulmates?</h2>
          <ul className="intro-modal-why">
            <li>❤️ Deeper Compatibility — Go beyond photos and swipes.</li>
            <li>🧠 Smarter Matching — Personality, values, lifestyle, and goals considered together.</li>
            <li>🎯 Better Matches — Focus your time on people with meaningful compatibility.</li>
            <li>🔒 You Stay in Control — You decide what you share and who you connect with.</li>
          </ul>

          <p className="intro-modal-disclaimer">
            FutureSoulmates provides compatibility insights, not guarantees of
            relationship success. DNA-based features are supplemental and, where
            noted, experimental — they are not a substitute for professional genetic
            counseling or medical advice.
          </p>

          <label className="intro-modal-agree">
            <input
              type="checkbox"
              checked={agreed}
              onChange={(e) => setAgreed(e.target.checked)}
            />
            I have read and agree to the Terms of Service and Privacy Policy.
          </label>
        </div>

        <div className="intro-modal-actions">
          <button
            className="intro-modal-btn"
            disabled={!agreed}
            onClick={handleClose}
          >
            Get Started
          </button>
        </div>
      </div>
    </div>
  );
};

export default IntroModal;